/**
 * 面标记工具函数
 * 颜色系统、面积计算、坐标转换、射线法点击检测
 */

// ========== 颜色系统 ==========

const POLYGON_COLORS = {
  red: { name: '红色', hex: '#E53935' },
  blue: { name: '蓝色', hex: '#1976D2' },
  green: { name: '绿色', hex: '#388E3C' },
};

const POLYGON_DEFAULT_COLOR_KEY = 'red';

function getPolygonStyle(colorKey) {
  const color = POLYGON_COLORS[colorKey] || POLYGON_COLORS[POLYGON_DEFAULT_COLOR_KEY];
  const { hex } = color;
  return {
    strokeColor: `${hex}AA`, // 描边 67% 不透明
    strokeWidth: 2,
    fillColor: `${hex}33`, // 填充 20% 不透明
  };
}

function getPolygonColorHex(colorKey) {
  const color = POLYGON_COLORS[colorKey] || POLYGON_COLORS[POLYGON_DEFAULT_COLOR_KEY];
  return color.hex;
}

function getPolygonColorOptions() {
  return Object.entries(POLYGON_COLORS).map(([key, val]) => ({
    key,
    name: val.name,
    hex: val.hex,
  }));
}

// ========== 面积计算（球面过剩法 - 梯形公式） ==========

function calcPolygonArea(points) {
  if (!points || points.length < 3) return 0;
  const n = points.length;
  // 球面过剩法：S = R² |Σ|
  // 将经纬度转为弧度
  const rad = Math.PI / 180;
  const pts = points.map(p => ({
    lat: p.latitude * rad,
    lng: p.longitude * rad,
  }));
  // 梯形公式
  let sum = 0;
  for (let i = 0; i < n; i += 1) {
    const j = (i + 1) % n;
    sum += (pts[j].lng - pts[i].lng) * (2 + Math.sin(pts[i].lat) + Math.sin(pts[j].lat));
  }
  // 地球半径 6371000 米
  const R = 6371000;
  return Math.abs(sum) * R * R / 2;
}

/**
 * 格式化面积为可读字符串（公制）
 */
function formatArea(areaSqM) {
  if (!areaSqM || areaSqM <= 0) return '';
  if (areaSqM >= 1000000) return `${(areaSqM / 1000000).toFixed(2)} km²`;
  if (areaSqM >= 10000) return `${(areaSqM / 10000).toFixed(2)} 万m²`;
  return `${Math.round(areaSqM)} m²`;
}

/**
 * 格式化面积为亩
 * 1亩 = 2000/3 平方米
 */
function formatAreaMu(areaSqM) {
  if (!areaSqM || areaSqM <= 0) return '';
  const mu = areaSqM / 2000 * 3;
  if (mu >= 10000) return `${(mu / 10000).toFixed(2)} 万亩`;
  return `${mu.toFixed(2)} 亩`;
}

// ========== 坐标格式转换 ==========

// [{latitude, longitude}] → [[lng, lat]]（小程序数据 → 高德 AMap.Polygon path）
function pointsToPath(points) {
  if (!points) return [];
  return points.map(p => [p.longitude, p.latitude]);
}

// [[lng, lat]] → [{latitude, longitude}]（高德 path → 小程序数据格式，保存时使用）
function pathToPoints(path) {
  if (!path) return [];
  return path.map(([lng, lat]) => ({ latitude: lat, longitude: lng }));
}

// ========== 射线法点击检测（备用，高德地图原生支持 click） ==========

function isPointInPolygon(lat, lng, points) {
  if (!points || points.length < 3) return false;
  let inside = false;
  for (let i = 0, j = points.length - 1; i < points.length; j = i, i += 1) {
    const xi = points[i].latitude;
    const yi = points[i].longitude;
    const xj = points[j].latitude;
    const yj = points[j].longitude;
    const intersect = ((yi > lng) !== (yj > lng))
      && (lat < (xj - xi) * (lng - yi) / (yj - yi) + xi);
    if (intersect) inside = !inside;
  }
  return inside;
}

// ========== 面中心点计算 ==========

function getPolygonCenter(points) {
  if (!points || points.length === 0) return null;
  let sumLat = 0;
  let sumLng = 0;
  points.forEach((p) => {
    sumLat += p.latitude;
    sumLng += p.longitude;
  });
  return {
    latitude: sumLat / points.length,
    longitude: sumLng / points.length,
  };
}

export {
  POLYGON_COLORS,
  POLYGON_DEFAULT_COLOR_KEY,
  getPolygonStyle,
  getPolygonColorHex,
  getPolygonColorOptions,
  calcPolygonArea,
  formatArea,
  formatAreaMu,
  pointsToPath,
  pathToPoints,
  isPointInPolygon,
  getPolygonCenter,
};
